// sagas.ts
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    fetchCancelOrderFailure,
    FetchCancelOrderRequestAction,
    fetchCancelOrderSuccess,
    fetchMatchDataFailure,
    fetchMatchDataRequest,
    fetchMatchDataSuccess,
    fetchOpenOrderFailure,
    fetchOpenOrderRequest,
    FetchOpenOrderRequestAction,
    fetchOpenOrderSuccess,
    fetchOrderFailure,
    FetchOrderRequestAction,
    fetchOrderSuccess,
    fetchUpdateOrderFailure,
    fetchUpdateOrderRequest,
    FetchUpdateOrderRequestAction,
    fetchUpdateOrderSuccess,
} from './actions';
import api from '../../../api-new/http';
import { appUrls } from '../../../api-new/urls';
import {
    FETCH_CANCEL_ORDER_REQUEST,
    FETCH_MATCH_DATA_REQUEST,
    FETCH_OPEN_ORDER_REQUEST,
    FETCH_ORDER_REQUEST,
    FETCH_UPDATE_ORDER_REQUEST,
} from './constants';
import { fetchUserAssetsRequest } from '../assets/actions';
import { notification } from 'antd';
import { fetchOrderBookRequest } from '../../public/stock-order-book/actions';
import { sendError } from '../../public/errorHandler';
import { getErrorSetup } from '../../../helpers/getErrorSetup';
import { fetchOrderHistoryRequest, fetchOrderMatchHistoryRequest } from '../stock-order-history';

export function* openOrderSaga(action: FetchOpenOrderRequestAction) {
    try {
        const data = yield call(() =>
            api.get(`${appUrls.tradeURL}/open-order/${action.payload}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('csrfToken')}`,
                },
            }),
        );
        if (data) {
            yield put(fetchOpenOrderSuccess(data.data.data));
        }
    } catch (error) {
        yield put(fetchOpenOrderFailure(error as any));
        yield put(
            sendError({
                error: getErrorSetup(error),
                processingType: 'alert',
            }),
        );
    }
}

export function* mathDataSaga(action: FetchOpenOrderRequestAction) {
    try {
        const data = yield call(() => api.get(`${appUrls.tradeURL}/match-histories/${action.payload}`, {}));
        if (data) {
            yield put(fetchMatchDataSuccess(data.data.data));
        }
    } catch (error) {
        yield put(fetchMatchDataFailure(error as any));
        yield put(
            sendError({
                error: getErrorSetup(error),
                processingType: 'alert',
            }),
        );
    }
}

export function* cancelOrderSaga(action: FetchCancelOrderRequestAction) {
    try {
        const { id, ticker } = action.payload;
        yield call(() =>
            api.post(
                `${appUrls.tradeURL}/cancel/${id}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('csrfToken')}`,
                    },
                },
            ),
        );
        notification.success({
            message: 'Order Canceled Successfully',
            description: 'Your order has been successfully canceled.',
            duration: 3,
        });

        (window as any).umami?.track('cancel_order', { orderId: id, ticker });

        yield put(fetchCancelOrderSuccess());
        yield put(fetchOpenOrderRequest(ticker));
        yield put(fetchOrderBookRequest(ticker));
        yield put(fetchUserAssetsRequest());
        yield put(fetchOrderHistoryRequest());
        yield put(fetchOrderHistoryRequest());
        yield put(fetchOrderMatchHistoryRequest());
    } catch (error) {
        yield put(fetchCancelOrderFailure(error as any));

        yield put(
            sendError({
                error: getErrorSetup(error),
                processingType: 'alert',
            }),
        );
    }
}

export function* fetchUpdateOrderSaga(action: FetchUpdateOrderRequestAction) {
    try {
        const { orderId, orderInfo } = action.payload;
        yield call(() =>
            api.post(
                `${appUrls.tradeURL}/update/${orderId}`,
                { ...orderInfo },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('csrfToken')}`,
                    },
                },
            ),
        );
        notification.success({
            message: 'Order Updated Successfully',
            description: 'Your order has been successfully updated.',
            duration: 3,
        });
        yield put(fetchUpdateOrderSuccess());
        yield put(fetchOpenOrderRequest(orderInfo.ticker));
        yield put(fetchOrderBookRequest(orderInfo.ticker));
        yield put(fetchUserAssetsRequest());
    } catch (error) {
        yield put(fetchUpdateOrderFailure(error as any));
        yield put(
            sendError({
                error: getErrorSetup(error),
                processingType: 'alert',
            }),
        );
    }
}
export function* orderSaga(action: FetchOrderRequestAction) {
    try {
        yield call(() =>
            api.post(`${appUrls.tradeURL}`, action.payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('csrfToken')}`,
                },
            }),
        );
        notification.success({
            message: 'Order Placed Successfully',
            description: 'Your order has been successfully placed and is being processed.',
            duration: 3, // Duration in seconds
        });

        (window as any).umami?.track('place_order', { ticker: action.payload.ticker, amount: action.payload.totalAmount });

        yield put(fetchOrderSuccess());
        yield put(fetchOpenOrderRequest(action.payload.ticker));
        yield put(fetchUserAssetsRequest());
        yield put(fetchMatchDataRequest(action.payload.ticker));
        yield put(fetchOrderBookRequest(action.payload.ticker));
        yield put(fetchOrderHistoryRequest());
        yield put(fetchOrderMatchHistoryRequest());

    } catch (error) {
        yield put(fetchOrderFailure(error as any));
        yield put(
            sendError({
                error: getErrorSetup(error),
                processingType: 'alert',
            }),
        );
    }
}

export function* rootStockOpenOrderSaga(): Generator {
    yield takeLatest(FETCH_OPEN_ORDER_REQUEST, openOrderSaga);
    yield takeLatest(FETCH_CANCEL_ORDER_REQUEST, cancelOrderSaga);
    yield takeLatest(FETCH_MATCH_DATA_REQUEST, mathDataSaga);
    yield takeLatest(FETCH_ORDER_REQUEST, orderSaga);
    yield takeLatest(FETCH_UPDATE_ORDER_REQUEST, fetchUpdateOrderSaga);
}
